import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaCreditCard, FaChartLine, FaHandshake, FaShieldAlt,
  FaExchangeAlt, FaCloud, FaArrowRight, FaCheckCircle
} from 'react-icons/fa';
import './Industries.css';

const industries = [
  {
    icon: <FaCreditCard />,
    title: 'Banking & Payments',
    desc: 'Modern digital banking infrastructure with real-time payments, UPI integrations, IMPS/NEFT/RTGS processing, and multi-bank reconciliation engines.',
    features: [
      'Core Banking Integration',
      'Real-Time Payment Rails',
      'UPI & NACH Solutions',
      'Multi-Currency Processing',
      'Fraud Detection Engine',
      'AML Compliance Tools',
    ],
    color: '#0056B3',
  },
  {
    icon: <FaHandshake />,
    title: 'Escrow & Trust Services',
    desc: 'Digital escrow platforms that securely hold funds during transactions, providing trust and transparency for B2B and B2C business models.',
    features: [
      'Conditional Fund Release',
      'Multi-Party Escrow',
      'Milestone-Based Payments',
      'Dispute Resolution System',
      'Regulatory Compliance',
      'Real-Time Notifications',
    ],
    color: '#1565C0',
  },
  {
    icon: <FaExchangeAlt />,
    title: 'Reconciliation & Settlements',
    desc: 'Automated reconciliation systems that match millions of transactions daily across payment gateways, banks, and internal ledgers with near-zero error rates.',
    features: [
      'Multi-Source Reconciliation',
      'Auto-Matching Algorithms',
      'Exception Management',
      'Settlement Processing',
      'Reporting & Analytics',
      'ERP Integration Ready',
    ],
    color: '#1976D2',
  },
  {
    icon: <FaChartLine />,
    title: 'Investment & Trading',
    desc: 'Trading platforms, portfolio management systems, and investment analytics tools built for brokers, asset managers, and retail investors.',
    features: [
      'Order Management System',
      'Portfolio Analytics',
      'Risk Management Engine',
      'Regulatory Reporting',
      'Market Data Integration',
      'Algo Trading Support',
    ],
    color: '#2196F3',
  },
  {
    icon: <FaShieldAlt />,
    title: 'Insurance Technology',
    desc: 'End-to-end insurtech platforms covering policy management, claims automation, premium collection, and digital underwriting workflows.',
    features: [
      'Policy Lifecycle Management',
      'Claims Automation',
      'Premium Collection',
      'Reinsurance Integration',
      'IoT Data Processing',
      'Customer Self-Service',
    ],
    color: '#1E88E5',
  },
  {
    icon: <FaCloud />,
    title: 'Cloud Finance Infrastructure',
    desc: 'Cloud-native financial platforms on AWS, Azure, and GCP with fintech-specific compliance, security controls, and high availability architectures.',
    features: [
      'PCI DSS Cloud Setup',
      'Multi-Cloud Strategy',
      'Data Residency Compliance',
      'Auto-Scaling Finance Apps',
      'Disaster Recovery',
      'Cost Optimization',
    ],
    color: '#0288D1',
  },
];

const verticals = [
  { icon: '🏦', name: 'Neo Banks' },
  { icon: '💳', name: 'Payment Aggregators' },
  { icon: '📱', name: 'Mobile Wallets' },
  { icon: '🏪', name: 'Merchant Platforms' },
  { icon: '🏛️', name: 'NBFCs & Lenders' },
  { icon: '📊', name: 'Stock Brokers' },
  { icon: '🌐', name: 'Forex Platforms' },
  { icon: '🔄', name: 'Remittance Firms' },
];

function Industries() {
  return (
    <div className="industries-page">
      <Helmet>
        <title>Industries We Serve | Techmascot Technologies</title>
        <meta name="description" content="Techmascot Technologies delivers tailored fintech solutions for Banking, NBFCs, Payment Aggregators, E-Commerce, Healthcare, Lending Platforms and more." />
        <link rel="canonical" href="https://www.techmascot.com/industries" />
        <meta property="og:title" content="Industries We Serve | Techmascot Technologies" />
        <meta property="og:description" content="Techmascot Technologies delivers tailored fintech solutions for Banking, NBFCs, Payment Aggregators, E-Commerce, Healthcare, Lending Platforms and more." />
        <meta property="og:url" content="https://www.techmascot.com/industries" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Page Banner */}
      <section className="page-banner">
        <div className="banner-bg">
          <div className="banner-grid"></div>
        </div>
        <div className="container">
          <div className="banner-content">
            <span className="tag">Our Domain Expertise</span>
            <h1>Industries We <span>Serve</span></h1>
            <p>
              Deep fintech expertise across banking, payments, insurance, investment,
              and cloud finance — we understand your industry's unique challenges.
            </p>
          </div>
        </div>
        <div className="banner-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,30 C480,60 960,0 1440,30 L1440,60 L0,60 Z" fill="#F8FAFF" />
          </svg>
        </div>
      </section>

      {/* Verticals */}
      <section className="verticals-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Who We Work With</span>
            <h2 className="section-title">Business <span>Verticals</span></h2>
          </div>
          <div className="verticals-grid">
            {verticals.map((v, i) => (
              <div key={i} className="vertical-card">
                <span className="vertical-emoji">{v.icon}</span>
                <span>{v.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Details */}
      <section className="industry-details-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Deep Dive</span>
            <h2 className="section-title">Industry <span>Solutions</span></h2>
            <p className="section-subtitle">
              Tailored technology solutions for each financial sector, built with
              domain expertise and regulatory compliance at the core.
            </p>
          </div>
          <div className="industry-cards-grid">
            {industries.map((ind, i) => (
              <div key={i} className="industry-detail-card">
                <div className="ind-card-icon" style={{ background: `${ind.color}15`, color: ind.color }}>
                  {ind.icon}
                </div>
                <h3>{ind.title}</h3>
                <p>{ind.desc}</p>
                <ul className="ind-features">
                  {ind.features.map((f, j) => (
                    <li key={j}>
                      <FaCheckCircle style={{ color: ind.color }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ind-cta">
        <div className="container">
          <div className="ind-cta-inner">
            <h2>Don't See Your Industry?</h2>
            <p>
              We work across all fintech and financial service verticals. Contact us to
              discuss how we can build a custom solution for your specific domain.
            </p>
            <Link to="/contact" className="btn-primary">
              Discuss Your Project <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Industries;
